/**
 * Admin tab. Only rendered for accounts with is_admin.
 *
 * Everything the server-rendered /admin page does, without needing a second
 * cookie login in a browser. That page stays as the break-glass surface for
 * when the SPA itself is what's broken.
 */

import { useEffect, useState } from 'react'

import { api, ApiError, type User } from '../api/client'
import { InviteFriend } from '../components/InviteFriend'
import { parseIso } from '../lib/time'

type AdminUser = {
  id: number
  username: string
  name: string
  email: string | null
  is_admin: boolean
  is_approved: boolean
  created_at: string | null
  last_session_at: string | null
  is_me: boolean
}

type Overview = {
  users: number
  approved: number
  sessions_this_week: number
  unused_invites: number
}

type AiSpend = {
  month: string
  cap_usd: number
  per_user_cap_usd: number
  spent_usd: number
  by_user: {
    user_id: number
    name: string
    calls: number
    cost_usd: number
    input_tokens: number
    output_tokens: number
    cache_read_tokens: number
    cache_write_tokens: number
  }[]
  recent: {
    endpoint: string
    model: string
    cost_usd: number
    cache_read_tokens: number
    ok: boolean
    error: string | null
    created_at: string | null
    user: string
  }[]
}

export function AdminScreen({ user }: { user: User }) {
  const [overview, setOverview] = useState<Overview | null>(null)
  const [users, setUsers] = useState<AdminUser[] | null>(null)
  const [ai, setAi] = useState<AiSpend | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadUsers = () =>
    api<{ users: AdminUser[] }>('/admin/users')
      .then((r) => setUsers(r.users))
      .catch(() => setUsers(null))

  useEffect(() => {
    api<Overview>('/admin/overview').then(setOverview).catch(() => setOverview(null))
    void loadUsers()
    api<AiSpend>('/admin/ai').then(setAi).catch(() => setAi(null))
  }, [])

  async function patchUser(id: number, body: Partial<AdminUser>) {
    setError(null)
    try {
      await api(`/admin/users/${id}`, { method: 'PATCH', body })
      await loadUsers()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Update failed.')
    }
  }

  const pctOfCap = ai && ai.cap_usd > 0 ? (ai.spent_usd / ai.cap_usd) * 100 : 0

  return (
    <>
      <h1>Admin</h1>

      {error && <div className="error">{error}</div>}

      {overview && (
        <div className="card">
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
            <Stat value={String(overview.approved)} label="active members" />
            <Stat value={String(overview.sessions_this_week)} label="sessions, 7 days" />
            <Stat value={String(overview.unused_invites)} label="unused invites" />
          </div>
        </div>
      )}

      <InviteFriend />

      <h2>Claude spend</h2>
      <div className="card">
        {ai === null ? (
          <p className="muted" style={{ margin: 0 }}>Needs a connection.</p>
        ) : (
          <>
            <div className="row">
              <span>
                <strong>${ai.spent_usd.toFixed(4)}</strong>
                <span className="muted"> of ${ai.cap_usd.toFixed(2)} this month</span>
              </span>
              <span className="muted">{ai.month}</span>
            </div>

            <div
              style={{
                height: 4,
                marginTop: '.5rem',
                background: 'var(--border)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, pctOfCap)}%`,
                  background: pctOfCap >= 80 ? 'var(--warn)' : 'var(--accent)',
                }}
              />
            </div>

            {ai.by_user.length > 0 ? (
              <ul className="plain" style={{ marginTop: '.75rem' }}>
                {ai.by_user.map((row) => (
                  <li key={row.user_id}>
                    <span>
                      {row.name}
                      <div className="muted" style={{ fontSize: '.78rem' }}>
                        {row.calls} call{row.calls === 1 ? '' : 's'} ·{' '}
                        {row.cache_read_tokens > 0
                          ? `${row.cache_read_tokens.toLocaleString()} cached in`
                          : 'no cache hits'}
                      </div>
                    </span>
                    <span>${row.cost_usd.toFixed(4)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted" style={{ marginBottom: 0 }}>No calls this month.</p>
            )}

            {ai.by_user.length > 0 &&
              ai.by_user.every((r) => r.cache_read_tokens === 0) && (
                <div
                  style={{
                    marginTop: '.6rem',
                    padding: '.5rem .7rem',
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--warn)',
                    color: 'var(--warn)',
                    fontSize: '.85rem',
                  }}
                >
                  No prompt-cache hits at all. If that persists across repeated
                  plan generations, something volatile has crept into the cached
                  system prompt and every call is paying roughly 10× what it
                  should.
                </div>
              )}

            {ai.recent.some((r) => !r.ok) && (
              <>
                <div className="muted" style={{ marginTop: '.75rem', fontSize: '.82rem' }}>
                  Recent failures
                </div>
                <ul className="plain" style={{ margin: 0 }}>
                  {ai.recent.filter((r) => !r.ok).slice(0, 3).map((r, i) => (
                    <li key={i}>
                      <span className="muted" style={{ fontSize: '.8rem' }}>
                        {r.endpoint}: {r.error?.slice(0, 80)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>

      <h2>Members</h2>
      <div className="card">
        {users === null ? (
          <p className="muted" style={{ margin: 0 }}>Needs a connection.</p>
        ) : (
          <ul className="plain" style={{ margin: 0 }}>
            {users.map((u) => (
              <li key={u.id} style={{ display: 'block' }}>
                <div className="row">
                  <span>
                    <strong>{u.name}</strong>
                    {u.is_admin && <span className="muted"> · admin</span>}
                    {u.is_me && <span className="muted"> · you</span>}
                    <div className="muted" style={{ fontSize: '.78rem' }}>
                      @{u.username}
                      {u.last_session_at
                        ? ` · last trained ${relative(u.last_session_at)}`
                        : ' · never trained'}
                    </div>
                  </span>
                  {!u.is_me && (
                    <button
                      className="secondary"
                      style={{
                        marginTop: 0, padding: '.25rem .6rem', minHeight: 0,
                        fontSize: '.8rem',
                        ...(u.is_approved
                          ? {}
                          : { color: 'var(--accent)', borderColor: 'var(--accent)' }),
                      }}
                      onClick={() => patchUser(u.id, { is_approved: !u.is_approved })}
                    >
                      {u.is_approved ? 'Revoke' : 'Approve'}
                    </button>
                  )}
                </div>
                {!u.is_approved && (
                  <div className="muted" style={{ fontSize: '.78rem' }}>
                    Revoked — can't sign in, history kept.
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="muted" style={{ fontSize: '.8rem', marginBottom: 0 }}>
          Revoking blocks sign-in but keeps their history, so it's reversible.
          Signed-in devices keep working until their token is used again.
        </p>
      </div>

      <h2>Server</h2>
      <div className="card">
        <ul className="plain" style={{ margin: 0 }}>
          <li><span>Signed in as</span><span className="muted">{user.username}</span></li>
          <li>
            <span>Full admin page</span>
            <a href="/admin" target="_blank" rel="noreferrer">open</a>
          </li>
          <li>
            <span>Install page</span>
            <a href="/get" target="_blank" rel="noreferrer">/get</a>
          </li>
        </ul>
        <p className="muted" style={{ fontSize: '.8rem', marginBottom: 0 }}>
          The server-rendered admin page needs a separate browser sign-in. It's
          the fallback for when this app is the thing that's broken.
        </p>
      </div>
    </>
  )
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div style={{ fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.1 }}>{value}</div>
      <div className="muted" style={{ fontSize: '.78rem' }}>{label}</div>
    </div>
  )
}

function relative(iso: string): string {
  const days = Math.floor((Date.now() - parseIso(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 14) return `${days} days ago`
  return parseIso(iso).toLocaleDateString([], { month: 'short', day: 'numeric' })
}
